"use strict";
/**
 * Shared Configuration for Aerotage Backend
 *
 * Centralizes all environment variable access for DynamoDB tables, S3 buckets,
 * and other AWS resources. Import from @aerotage/shared instead of accessing
 * process.env directly.
 *
 * POLICY: No fallbacks, no defaults. Every config value must be backed by an
 * explicitly-set environment variable. If the env var is missing, the code
 * throws immediately with a clear error message naming the missing variable.
 *
 * LAZY EVALUATION: All config objects use Object.defineProperty getters so
 * env vars are only read when first accessed. Each Lambda only crashes if it
 * accesses a config value whose env var is missing -- not at import time for
 * vars it never uses.
 *
 * Usage:
 *   import { TABLES, BUCKETS, AWS_CONFIG } from '@aerotage/shared';
 *   const project = await dynamoDBService.get(TABLES.PROJECTS, { projectId });
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TERMS_CONFIG = exports.getDynamoDBConfig = exports.isLocalDynamoDB = exports.COGNITO_CONFIG = exports.STEP_FUNCTIONS_CONFIG = exports.AI_CONFIG = exports.AWS_CONFIG = exports.BUCKETS = exports.TABLES = void 0;
const env_1 = require("./env");
/**
 * Create a config object where each property lazily evaluates requireEnv()
 * on first access. Values are cached after the first read.
 *
 * The generic <K extends string> infers the literal key union from the spec
 * object, giving autocomplete on e.g. TABLES.PROJECTS.
 */
function defineLazyEnvObject(spec) {
    const obj = {};
    for (const [prop, envKey] of Object.entries(spec)) {
        let cached;
        let initialized = false;
        Object.defineProperty(obj, prop, {
            get() {
                if (!initialized) {
                    cached = (0, env_1.requireEnv)(envKey);
                    initialized = true;
                }
                return cached;
            },
            enumerable: true,
        });
    }
    return obj;
}
/**
 * DynamoDB Table Names
 *
 * All table names are derived from environment variables set by CDK.
 * The naming convention is: devproc-{resource}-{environment}
 */
exports.TABLES = defineLazyEnvObject({
    // Core tables
    PROJECTS: 'PROJECTS_TABLE_NAME',
    DOCUMENTS: 'DOCUMENTS_TABLE_NAME',
    DESIGN_DOCUMENTS: 'DESIGN_DOCUMENTS_TABLE_NAME',
    USERS: 'USERS_TABLE_NAME',
    CUSTOMERS: 'CUSTOMERS_TABLE_NAME',
    // Team & Access
    PROJECT_TEAM_MEMBERS: 'PROJECT_TEAM_MEMBERS_TABLE_NAME',
    INVITATIONS: 'INVITATIONS_TABLE_NAME',
    ACCESS_LOGS: 'ACCESS_LOGS_TABLE_NAME',
    // Approval Workflow
    APPROVALS: 'APPROVALS_TABLE_NAME',
    DOCUMENT_EDIT_SESSIONS: 'DOCUMENT_EDIT_SESSIONS_TABLE_NAME',
    // AI & Conversations
    AI_CONVERSATIONS: 'AI_CONVERSATIONS_TABLE_NAME',
    AI_PLAN_JOBS: 'AI_PLAN_JOBS_TABLE_NAME',
    AI_MEMORIES: 'AI_MEMORIES_TABLE_NAME',
    // Reference & Company Docs
    REFERENCE_DOCUMENTS: 'REFERENCE_DOCUMENTS_TABLE_NAME',
    COMPANY_DOCUMENTS: 'COMPANY_DOCUMENTS_TABLE_NAME',
    // Supply Chain
    SUPPLIERS: 'SUPPLIERS_TABLE_NAME',
    PURCHASE_ORDERS: 'PURCHASE_ORDERS_TABLE_NAME',
    RECEIVING_INSPECTIONS: 'RECEIVING_INSPECTIONS_TABLE_NAME',
    // Batch Jobs
    PDF_BATCH_JOBS: 'PDF_BATCH_JOBS_TABLE_NAME',
    // AI Development (Cloud Development Environment)
    CODING_PROJECTS: 'CODING_PROJECTS_TABLE_NAME',
    // Reliability Data (ARP4761A Safety Assessment)
    RELIABILITY_SOURCES: 'RELIABILITY_SOURCES_TABLE_NAME',
    FAILURE_RATES: 'FAILURE_RATES_TABLE_NAME',
    RELIABILITY_LINKS: 'RELIABILITY_LINKS_TABLE_NAME',
    // WebSocket Auth
    WEBSOCKET_CONNECTIONS: 'WEBSOCKET_CONNECTIONS_TABLE_NAME',
    // Billing & Usage
    USAGE_RECORDS: 'USAGE_RECORDS_TABLE_NAME',
    DAILY_USAGE_AGGREGATES: 'DAILY_USAGE_AGGREGATES_TABLE_NAME',
    SUBSCRIPTIONS: 'SUBSCRIPTIONS_TABLE_NAME',
    WEBHOOK_EVENTS: 'WEBHOOK_EVENTS_TABLE_NAME',
});
/**
 * S3 Bucket Names
 *
 * All bucket names are derived from environment variables set by CDK.
 * The naming convention is: devproc-{resource}-{environment}
 */
exports.BUCKETS = defineLazyEnvObject({
    // Document Storage
    GENERATED_DOCUMENTS: 'GENERATED_BUCKET_NAME',
    DOCUMENT_EMBEDDINGS: 'DOCUMENT_EMBEDDINGS_BUCKET_NAME',
    // Reference Documents
    REFERENCE_PDFS: 'REFERENCE_PDFS_BUCKET_NAME',
    REFERENCE_MARKDOWN: 'REFERENCE_MARKDOWN_BUCKET_NAME',
    REFERENCE_EMBEDDINGS: 'REFERENCE_EMBEDDINGS_BUCKET_NAME',
    // Company Documents
    COMPANY_DOCS: 'COMPANY_DOCS_BUCKET_NAME',
    COMPANY_EMBEDDINGS: 'COMPANY_EMBEDDINGS_BUCKET_NAME',
    // Frontend
    FRONTEND: 'FRONTEND_BUCKET_NAME',
    // Exports
    EXPORTS: 'EXPORTS_BUCKET_NAME',
});
/**
 * AWS Configuration
 */
exports.AWS_CONFIG = defineLazyEnvObject({
    REGION: 'AWS_REGION',
});
/**
 * AI Configuration
 */
exports.AI_CONFIG = defineLazyEnvObject({
    EMBEDDING_MODEL: 'EMBEDDING_MODEL',
    BEDROCK_MODEL: 'BEDROCK_MODEL',
});
/**
 * Step Functions Configuration
 * Used for long-running agentic processes (replaces Lambda self-chaining)
 */
exports.STEP_FUNCTIONS_CONFIG = defineLazyEnvObject({
    AGENT_STATE_MACHINE_ARN: 'AGENT_STATE_MACHINE_ARN',
});
/**
 * Cognito Configuration
 */
exports.COGNITO_CONFIG = defineLazyEnvObject({
    USER_POOL_ID: 'USER_POOL_ID',
    CLIENT_ID: 'USER_POOL_CLIENT_ID',
});
/**
 * Helper to check if running with local DynamoDB.
 * Reads process.env directly -- DYNAMODB_ENDPOINT is a feature flag,
 * not a required config value.
 */
const isLocalDynamoDB = () => {
    return !!process.env.DYNAMODB_ENDPOINT;
};
exports.isLocalDynamoDB = isLocalDynamoDB;
/**
 * Helper to get DynamoDB config for client initialization.
 * Uses AWS_CONFIG.REGION (lazy, throws if missing) for the region,
 * and reads DYNAMODB_ENDPOINT from process.env directly for local override.
 */
const getDynamoDBConfig = () => {
    const config = {
        region: exports.AWS_CONFIG.REGION,
    };
    const localEndpoint = process.env.DYNAMODB_ENDPOINT;
    if (localEndpoint) {
        config.endpoint = localEndpoint;
        config.credentials = {
            accessKeyId: 'local',
            secretAccessKey: 'local',
        };
    }
    return config;
};
exports.getDynamoDBConfig = getDynamoDBConfig;
/**
 * Terms & Privacy version tracking.
 * Bump these when the legal pages are updated — triggers re-acceptance for all users.
 * Version format matches the "Last updated" date on the legal pages.
 *
 * URLs are environment-specific so only paths are stored here.
 * Frontend: constructs full URL from VITE_WEBSITE_URL env var.
 * Backend:  constructs full URL from WEBSITE_URL env var (set by CDK).
 */
exports.TERMS_CONFIG = {
    CURRENT_TERMS_VERSION: '2026-02-19',
    CURRENT_PRIVACY_VERSION: '2026-02-19',
    TERMS_PATH: '/terms',
    PRIVACY_PATH: '/privacy',
};
//# sourceMappingURL=config.js.map